import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';

import { useSnackbar } from 'v3/components/Snackbar';
import { useModal } from 'hooks';
import moment from 'moment';
import ConfirmModal from './Modal/ConfirmModal';

import { cooperplaceApi } from 'services/api';

import { FaImage } from 'react-icons/fa';

import {
  Text,
  Card,
  Upload,
  CardToWarn,
  LoadingProgressBar
} from 'v3/components';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { validate } from './validator'

import GeneralDate from './GeneralDate';
import OwnerData from './OwnerData';
import AnttData from './AnttData';

const WagonsRegisterOcr = forwardRef(({
  data,
  setData,
  frontImageCrlv,
  setFrontImageCrlv,
  errors,
  setErrors,
  setSaveLoading,
  saveLoading,
  wagonKey,
  sendResponse,
  handleClose
}, ref) => {

  const snackbar = useSnackbar();
  const confirmModal = useModal();

  const [uploadResetKey, setUploadResetKey] = useState(0);
  const [pendingOcrData, setPendingOcrData] = useState(null)

  const [loading, setLoading] = useState(false);

  function validateCrlvFile(file) {
    return file?.type === 'application/pdf' && file?.name?.toLowerCase().endsWith('.pdf');
  }

  async function updateAttachment(wagonsId) {
    try {

      const attachmentsFiles = new FormData();

      const fileName = frontImageCrlv[0]?.name
        .replaceAll('_', '-')
        .split('.')
        .slice(0, -1)
        .join('.');

      const fileNameFormatted = `${Date.now()}.${frontImageCrlv[0]?.type.split('/')[1]
        }`;
      const name = fileName
        ? `${fileName}-${fileNameFormatted}`
        : fileNameFormatted;
      const newFile = new File([frontImageCrlv[0]], name, {
        type: frontImageCrlv[0]?.type,
      });

      const type = frontImageCrlv[0]?.type;
      const description = 'CRLV - Documento de Registro e Licenciamento de Veículo';
      attachmentsFiles.append('file', newFile);
      attachmentsFiles.append('type', type);
      attachmentsFiles.append('description', description);
      attachmentsFiles.append('isOcrImported', true);
      attachmentsFiles.append('dueDate', new Date().toISOString());

      await cooperplaceApi.post(
        `wagons/${wagonsId}/attachment`,
        attachmentsFiles
      );

    } catch (error) {
      snackbar.show(<Text>Não foi possível atualizar o anexo.</Text>, {
        type: 'error',
      });
    }
  }

  function applyOcrData(ocrData, formatOcrData, wagon) {
    setData(old => ({
      ...old,
      generalData: {
        ...old.generalData,
        branchOffices: wagon?.branchOffices || [],
        plate: ocrData?.plate,
        renavam: ocrData?.renavam,
        yearModel: ocrData?.yearModel,
        yearManufacture: ocrData?.yearManufacture,
        crlv: ocrData?.crlv,
        color: formatOcrData?.color,
        axis: Number(ocrData?.axis),
        brand: formatOcrData?.brand,
        model: formatOcrData?.model,
        chassi: ocrData?.chassi,
        city: formatOcrData?.city,
        totalGrossWeight: ocrData?.grossWeight,
        bodyType: wagon?.bodyType,
        implementType: wagon?.implementType,
        tareMass: wagon?.tareMass,
        cubage: wagon?.cubage,
      },
      ownerData: {
        ...old.ownerData,
        owner: formatOcrData?.owner,
        beneficiary: formatOcrData?.owner
      },
      anttData: {
        ...old.anttData,
        antt: wagon?.antt,
        anttAdherence: wagon?.anttAdherence ? moment.utc(wagon?.anttAdherence).format('YYYY-MM-DD') : '',
        anttType: wagon?.anttTypes,
        anttOwner: wagon?.ownerAntt ? {
          id: wagon?.ownerAntt?.id,
          name: wagon?.ownerAntt?.socialName,
          label: wagon?.ownerAntt?.socialName + (wagon?.ownerAntt?.cpfCnpj ? ` - ${wagon.ownerAntt.cpfCnpj}` : '')
        } : null
      },
    }));
  }

  const extractOcrCrlv = async (file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);

      setLoading(true);
      const { data } = await cooperplaceApi.post('/wagons/extract/document/ocr', formData);

      const ocrData = data?.ocrData
      const formatOcrData = data?.formatData
      const databaseData = data?.databaseData
      const confirmUpdate = data?.isUpdate
      const wagon = data?.wagon

      if (confirmUpdate) {
        setPendingOcrData({ ocrData, formatOcrData, databaseData, wagon });
        confirmModal.open();
      } else {
        applyOcrData(ocrData, formatOcrData, wagon);
        snackbar.show(<Text>CRLV processado com sucesso!</Text>, {
          type: 'success',
        });
      }

    } catch (err) {

      setFrontImageCrlv([]);
      setUploadResetKey(k => k + 1);

      const message = err?.response?.data?.error;

      snackbar.show(<Text>{message || 'Erro ao processar o CRLV'}</Text>, {
        type: 'error',
      });

      setLoading(false);

    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (frontImageCrlv?.length) {
      const file = frontImageCrlv[0];
      extractOcrCrlv(file);
    }
  }, [frontImageCrlv])


  async function handleRegister() {

    try {

      setSaveLoading(true);

      const isDataValid = await validate(data, setErrors);

      if (!isDataValid) {
        snackbar.show(<Text>Verifique os campos obrigatórios!</Text>, {
          type: 'error',
        });
        setSaveLoading(false);
        return;
      }

      const payload = {
        generalData: {
          ...data.generalData,
          yearModel: Number(data.generalData.yearModel),
          yearManufacture: Number(data.generalData.yearManufacture),
        },
        anttData: {
          ...data.anttData,
          anttOwner: {
            id: data.anttData?.anttOwner?.id,
            name: data.anttData?.anttOwner?.social_name || data.anttData?.anttOwner?.name,
          }
        },
        ownerData: {
          ...data.ownerData,
          owner: {
            id: data.ownerData?.owner?.id,
            name: data.ownerData?.owner?.social_name || data.ownerData?.owner?.name,
          },
          beneficiary: {
            id: data.ownerData?.beneficiary?.id,
            name: data.ownerData?.beneficiary?.social_name || data.ownerData?.beneficiary?.name,
          }
        },
      };

      const response = await cooperplaceApi.post('/wagons/register-or-update', payload);

      if (frontImageCrlv?.length && response?.data?.id) {
        await updateAttachment(response.data.id);
      }

      sendResponse({ wagonKey, response });

      snackbar.show(<Text>Implemento registrado com sucesso!</Text>, {
        type: 'success',
      });

      handleClose();

    } catch (err) {
      const message = err?.response?.data?.error;

      snackbar.show(<Text>{message || 'Erro ao registrar o implemento'}</Text>, {
        type: 'error',
      });

      setSaveLoading(false);

    } finally {
      setSaveLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    save: handleRegister,
  }));

  return (
    <>
      <div style={{ position: 'relative' }}>
        {loading && (
          <LoadingProgressBar text={loading ? 'Processando CRLV...' : 'Registrando implemento...'} />
        )}
        <Row style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
          <Col xl={5} lg={5} md={12} xs={12}>
            <CardToWarn
              text="Realize apenas o upload do CRLV,
              a IA irá extrair automaticamente os dados para o cadastro do implemento.
              Após o processamento, revise as informações extraídas e preencha os campos restantes para concluir o cadastro."
            />
            <Card
              header={
                <Text color="#464E5F" type="header">
                  Documentos
                </Text>
              }
            >
              <Upload
                key={uploadResetKey}
                disabled={saveLoading}
                sizeIcon={50}
                icon={<FaImage size={50} />}
                onChange={event =>
                  setFrontImageCrlv(event.target.files)
                }
                onFilesChange={files => {
                  if (!files?.length) {
                    setFrontImageCrlv([]);

                    setData(old => ({
                      ...old,
                      generalData: {
                        ...old.generalData,
                        plate: '',
                        renavam: '',
                        yearModel: '',
                        yearManufacture: '',
                        crlv: '',
                        color: null,
                        axis: '',
                        brand: null,
                        model: null,
                        chassi: '',
                        fuel: null,
                        city: null,
                        totalGrossWeight: null
                      },
                      ownerData: {
                        ...old.ownerData,
                        owner: null,
                        beneficiary: null
                      }
                    }));
                  }
                }}
                value={frontImageCrlv}
                id="imgCrlv"
                name="imgCrlv"
                accept={['.pdf']}
                label="Arquivo máximo de 10MB"
                maxFileSize={10485760}
                showPreview={true}
                validate={validateCrlvFile}
                onValidationError={() =>
                  snackbar.show(<Text>Apenas arquivos PDF são aceitos para o processamento.</Text>, {
                    type: 'error',
                  })
                }
              />
              <Col style={{ marginTop: 8 }}>
                {!frontImageCrlv?.length && (
                  <CardToWarn
                    text="⛔ Apenas arquivos no formato PDF são aceitos. Faça o upload do CRLV em PDF para continuar."
                    backgroundColor="#FDECEA"
                    borderColor="#F44336"
                    textColor="#C62828"
                  />
                )}
              </Col>
            </Card>
          </Col>

          <Col xl={7} lg={7} md={12} xs={12} key={frontImageCrlv?.length ? 'with-image' : 'no-image'}>

            <GeneralDate
              setData={generalData => setData({ ...data, generalData })}
              data={data?.generalData}
              errors={errors?.generalData}
              fildsDisabled={!frontImageCrlv?.length}
              disabled={saveLoading}
              frontImageCrlv={frontImageCrlv}
            />

            <AnttData
              setData={anttData => setData({ ...data, anttData })}
              data={data?.anttData}
              errors={errors?.anttData}
              disabled={saveLoading}
            />

            <OwnerData
              setData={ownerData => setData({ ...data, ownerData })}
              data={data?.ownerData}
              errors={errors?.ownerData}
              disabled={saveLoading}
            />
          </Col>
        </Row>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.close}
        data={pendingOcrData?.databaseData}
        onConfirm={() => {
          if (pendingOcrData) {
            applyOcrData(pendingOcrData.ocrData, pendingOcrData.formatOcrData, pendingOcrData.wagon);
            snackbar.show(<Text>CNH processada com sucesso!</Text>, { type: 'success' });
            setPendingOcrData(null);
          }
          confirmModal.close();
        }}
        setFrontImageCnh={() => { setFrontImageCrlv([]); setUploadResetKey(k => k + 1); }}
      />
    </>
  );
})

export default WagonsRegisterOcr;
